// «التحليل الذكي» — briefs over the whole platform. Numbers come from the data
// pack the server computed; the AI only writes the words around them. Advice is
// the super admin's alone: the server strips it from everyone else's payload,
// this page only decides whether to show the tab.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, RefreshCw, KeyRound, Loader2, FileText, Lightbulb, Layers, Store, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AI } from "@/constants/testIds/ai";
import { fmtTemplate, fmtWhen } from "@/components/ai-analysis/aiText";
import { AdviceSection, BranchesSection, ExecutiveSection, ModulesSection, PartialBanner, QualitySection } from "@/components/ai-analysis/BriefView";
import { HistoryView, SettingsCard, fmtUsd, periodLabel } from "@/components/ai-analysis/HistoryView";

const triggerClass =
    "gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

const POLL_MS = 5000;

export default function AiAnalysisPage() {
    const { t, lang, dir } = useI18n();
    const { isSuperAdmin } = useAuth();
    const [params, setParams] = useSearchParams();
    const [status, setStatus] = useState(null);
    const [briefs, setBriefs] = useState([]);
    const [brief, setBrief] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("day");
    const [starting, setStarting] = useState(false);
    const [now, setNow] = useState(Date.now());
    const wasRunning = useRef(false);

    const loadStatus = useCallback(async () => {
        const r = await api.get("/ai/status");
        setStatus(r.data);
        return r.data;
    }, []);

    const loadBriefs = useCallback(async () => {
        const r = await api.get("/ai/briefs", { params: { limit: 60 } });
        setBriefs(r.data.briefs || []);
        return r.data.briefs || [];
    }, []);

    const openBrief = useCallback(async (id) => {
        const r = id ? await api.get(`/ai/briefs/${id}`) : await api.get("/ai/briefs/latest");
        setBrief(r.data.brief || null);
    }, []);

    const reload = useCallback(async (selectLatest = false) => {
        try {
            const [, list] = await Promise.all([loadStatus(), loadBriefs()]);
            const wanted = selectLatest ? null : params.get("brief");
            const pick = wanted && list.find((b) => b.id === wanted && b.status === "done") ? wanted : null;
            await openBrief(pick);
        } catch (e) {
            toast.error(apiErrorText(e));
        } finally {
            setLoading(false);
        }
    }, [loadStatus, loadBriefs, openBrief, params]);

    useEffect(() => { reload(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // While a brief is being prepared: poll, then show the new one when it lands.
    const running = status?.running;
    useEffect(() => {
        if (!running) {
            if (wasRunning.current) {
                wasRunning.current = false;
                reload(true);
            }
            return undefined;
        }
        wasRunning.current = true;
        const id = setInterval(() => { loadStatus().catch(() => {}); }, POLL_MS);
        return () => clearInterval(id);
    }, [running, loadStatus, reload]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    const cooldownLeft = useMemo(() => {
        if (!status?.last_on_demand_at || !status?.cooldown_minutes) return 0;
        const until = new Date(status.last_on_demand_at).getTime() + status.cooldown_minutes * 60000;
        return Math.max(0, Math.ceil((until - now) / 60000));
    }, [status, now]);

    const runNow = async () => {
        setStarting(true);
        try {
            await api.post("/ai/run", { period });
            toast.success(t("ai_run_started"));
            await loadStatus();
            await loadBriefs();
        } catch (e) {
            toast.error(apiErrorText(e));
            loadStatus().catch(() => {});
        } finally {
            setStarting(false);
        }
    };

    const selectBrief = async (id) => {
        const next = new URLSearchParams(params);
        next.set("brief", id);
        setParams(next, { replace: true });
        try {
            await openBrief(id);
        } catch (e) {
            toast.error(apiErrorText(e));
        }
    };

    const tabs = ["executive", ...(brief?.can_see_advice ? ["advice"] : []), "modules", "branches", "quality", "history"];
    const wantedTab = params.get("tab");
    const tab = tabs.includes(wantedTab) ? wantedTab : "executive";
    const setTab = (v) => {
        const next = new URLSearchParams(params);
        if (v === "executive") next.delete("tab"); else next.set("tab", v);
        setParams(next, { replace: true });
    };
    const keyMissing = status && !status.key_configured;
    const canRun = !!status?.can_run;
    const doneBriefs = briefs.filter((b) => b.status === "done");

    const runAction = canRun ? (
        <div className="flex w-full items-center gap-2 sm:w-auto">
            <Select value={period} onValueChange={setPeriod} dir={dir} disabled={!!running || starting}>
                <SelectTrigger data-testid={AI.runPeriod} className="h-9 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {["day", "week", "month"].map((p) => <SelectItem key={p} value={p}>{t(`ai_period_${p}`)}</SelectItem>)}
                </SelectContent>
            </Select>
            <Button data-testid={AI.runBtn} onClick={runNow} className="flex-1 sm:flex-none"
                    disabled={keyMissing || !!running || starting || cooldownLeft > 0}>
                {running || starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {t("ai_run_now")}
            </Button>
        </div>
    ) : null;

    const meta = brief ? (
        <span data-testid={AI.briefMeta} className="text-xs text-[color:var(--ink-muted)]">
            {t(`ai_kind_${brief.kind}`)} · {periodLabel(brief, lang)} · {t("ai_generated")} {fmtWhen(brief.generated_at, lang)}
            {brief.mode ? <> · {t(`ai_mode_${brief.mode}`)}</> : null}
            {brief.cost_usd !== undefined && brief.cost_usd !== null ? <> · {t("ai_cost")} ${fmtUsd(brief.cost_usd)}</> : null}
            {isSuperAdmin && status?.month_spend_usd !== undefined ? <> · {t("ai_month_spend")} ${fmtUsd(status.month_spend_usd)}</> : null}
        </span>
    ) : null;

    return (
        <div data-testid={AI.page} className="mx-auto max-w-5xl space-y-4">
            <PageHeader icon={Sparkles} title={t("ai_title")} subtitle={t("ai_subtitle")} meta={meta} primaryAction={runAction} />

            {keyMissing ? (
                <div data-testid={AI.keyMissing} className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                    <KeyRound className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                        <p className="font-bold">{t("ai_key_missing")}</p>
                        <p className="mt-0.5 text-sm">{t("ai_key_missing_hint")}</p>
                    </div>
                </div>
            ) : null}
            {running ? (
                <div data-testid={AI.running} className="flex items-center gap-2 rounded-xl surface-2 p-3 text-sm text-[color:var(--ink)]">
                    <Loader2 className="h-4 w-4 animate-spin text-[color:var(--brand-orange)]" /> {t("ai_running")}
                </div>
            ) : null}
            {canRun && cooldownLeft > 0 && !running ? (
                <p data-testid={AI.cooldown} className="text-xs text-[color:var(--ink-muted)]">{fmtTemplate(t("ai_cooldown_until"), { m: cooldownLeft })}</p>
            ) : null}

            {doneBriefs.length > 1 ? (
                <Select value={brief?.id || ""} onValueChange={selectBrief} dir={dir}>
                    <SelectTrigger data-testid={AI.briefSelect} className="w-full sm:w-96"><SelectValue placeholder={t("ai_choose_brief")} /></SelectTrigger>
                    <SelectContent>
                        {doneBriefs.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                                {t(`ai_kind_${b.kind}`)} · {periodLabel(b, lang)} · {fmtWhen(b.generated_at, lang)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : null}

            {loading ? (
                <div className="space-y-3"><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>
            ) : !brief ? (
                <div data-testid={AI.empty} className="flex flex-col items-center gap-2 rounded-xl surface-2 px-4 py-12 text-center">
                    <Sparkles className="h-8 w-8 text-[color:var(--brand-orange)]" />
                    <p className="font-bold text-[color:var(--ink)]">{t("ai_no_brief")}</p>
                    <p className="max-w-md text-sm text-[color:var(--ink-muted)]">{t("ai_no_brief_hint")}</p>
                    {briefs.length ? (
                        <div className="mt-4 w-full max-w-xl text-start">
                            <HistoryView briefs={briefs} selectedId={null} onSelect={selectBrief} isSuper={isSuperAdmin} lang={lang} t={t} monthSpend={status?.month_spend_usd} />
                        </div>
                    ) : null}
                </div>
            ) : (
                <Tabs value={tab} onValueChange={setTab} dir={dir} className="space-y-4">
                    <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto sm:rounded-full">
                        <TabsTrigger data-testid={AI.tab("executive")} value="executive" className={triggerClass}><FileText className="h-4 w-4" />{t("ai_tab_executive")}</TabsTrigger>
                        {brief.can_see_advice ? (
                            <TabsTrigger data-testid={AI.tab("advice")} value="advice" className={triggerClass}><Lightbulb className="h-4 w-4" />{t("ai_tab_advice")}</TabsTrigger>
                        ) : null}
                        <TabsTrigger data-testid={AI.tab("modules")} value="modules" className={triggerClass}><Layers className="h-4 w-4" />{t("ai_tab_modules")}</TabsTrigger>
                        <TabsTrigger data-testid={AI.tab("branches")} value="branches" className={triggerClass}><Store className="h-4 w-4" />{t("ai_tab_branches")}</TabsTrigger>
                        <TabsTrigger data-testid={AI.tab("quality")} value="quality" className={triggerClass}><ShieldCheck className="h-4 w-4" />{t("ai_tab_quality")}</TabsTrigger>
                        <TabsTrigger data-testid={AI.tab("history")} value="history" className={triggerClass}><History className="h-4 w-4" />{t("ai_tab_history")}</TabsTrigger>
                    </TabsList>
                    <PartialBanner brief={brief} lang={lang} t={t} />
                    <TabsContent value="executive" className="mt-0"><ExecutiveSection brief={brief} lang={lang} t={t} /></TabsContent>
                    {brief.can_see_advice ? (
                        <TabsContent value="advice" className="mt-0"><AdviceSection brief={brief} lang={lang} t={t} /></TabsContent>
                    ) : null}
                    <TabsContent value="modules" className="mt-0"><ModulesSection brief={brief} lang={lang} t={t} /></TabsContent>
                    <TabsContent value="branches" className="mt-0"><BranchesSection key={brief.id} brief={brief} lang={lang} t={t} dir={dir} /></TabsContent>
                    <TabsContent value="quality" className="mt-0"><QualitySection brief={brief} lang={lang} t={t} /></TabsContent>
                    <TabsContent value="history" className="mt-0 space-y-4">
                        <HistoryView briefs={briefs} selectedId={brief.id} onSelect={selectBrief} isSuper={isSuperAdmin} lang={lang} t={t} monthSpend={status?.month_spend_usd} />
                        {isSuperAdmin ? <SettingsCard settings={status?.settings} onSaved={() => loadStatus()} t={t} /> : null}
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
}
